/** @format */

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
	Avatar,
	Box,
	Button,
	Card,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ContributionModal } from "../allComponents/AllComponents";
import { MissedContributionsWrapper } from "../missed-contributions";
import {
	getContributionStatus,
	getCurrentCycleOfCircle,
} from "../../redux/actions/contributionAction";
import { selectMemberContribution } from "../../redux/selectors/contributionSelector";
import { AppDispatch } from "../../redux/store/store";
import { IContributionStatus } from "../../utility/types/contributionType";
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";
import {
	ContributionAvailability,
	getContributionAvailability,
	getNextContributionDate,
} from "../../utility/utils/contributionAvailability";

const WEEK_DAYS = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

const MISSED_GRADIENT = "linear-gradient(135deg, #f97316 0%, #ef4444 100%)";

// Below ~360px (e.g. iPhone SE) keep actions on one line
const NARROW = "@media (max-width: 359.95px)";

const actionButtonSx = {
	minHeight: 48,
	borderRadius: "12px",
	textTransform: "none",
	fontWeight: 700,
	fontSize: "0.95rem",
	whiteSpace: "nowrap",
	[NARROW]: { fontSize: "0.85rem", px: 1 },
} as const;

type Tone = "success" | "warning" | "neutral";

const TONE_COLOR: Record<Tone, string> = {
	success: "#0f9f6e",
	warning: "#c2410c",
	neutral: "#64748b",
};

interface StatusLine {
	tone: Tone;
	icon: React.ReactNode;
	text: string;
}

const describeStatus = (
	availability: ContributionAvailability,
	circle: any,
	currentCycle: any,
): StatusLine => {
	const iconSx = { fontSize: 18 };
	switch (availability.reason) {
		case null:
			return {
				tone: "success",
				icon: <EventRoundedIcon sx={iconSx} />,
				text: "Contribution due today",
			};
		case "already_contributed":
			return {
				tone: "success",
				icon: <CheckCircleRoundedIcon sx={iconSx} />,
				text: "You've contributed today",
			};
		case "not_contribution_day": {
			const next = getNextContributionDate(
				circle?.configuration?.contributionDays,
			);
			return {
				tone: "neutral",
				icon: <EventRoundedIcon sx={iconSx} />,
				text:
					next ?
						`Next contribution ${next.format("ddd, MMM D")}`
					:	"Not a contribution day",
			};
		}
		case "cycle_not_started":
			return {
				tone: "neutral",
				icon: <EventRoundedIcon sx={iconSx} />,
				text: `Cycle starts ${moment.utc(currentCycle?.startDate).format("ddd, MMM D")}`,
			};
		case "cycle_ended":
			return {
				tone: "neutral",
				icon: <InfoOutlinedIcon sx={iconSx} />,
				text: "This cycle has ended",
			};
		case "no_cycle":
			return {
				tone: "neutral",
				icon: <InfoOutlinedIcon sx={iconSx} />,
				text: "No active contribution cycle",
			};
		default:
			return {
				tone: "neutral",
				icon: <InfoOutlinedIcon sx={iconSx} />,
				text: availability.message,
			};
	}
};

interface CircleContributionCardProps {
	circle: any;
	currentUserId: string;
	onViewCircle: (circleId: string) => void;
	onContributionSuccess?: () => void;
}

const CircleContributionCard = ({
	circle,
	currentUserId,
	onViewCircle,
	onContributionSuccess,
}: CircleContributionCardProps) => {
	const dispatch = useDispatch<AppDispatch>();

	const [openContributionModal, setOpenContributionModal] = useState(false);
	const [missedContributionAmount, setMissedContributionAmount] = useState<
		number | null
	>(null);
	const [missedDates, setMissedDates] = useState<string[]>([]);
	const [isContributionDayToday, setIsContributionDayToday] = useState(false);
	const [isTodayAlreadyPaid, setIsTodayAlreadyPaid] = useState(false);
	// Disable immediately after a successful payment, before the status refetch lands
	const [justContributed, setJustContributed] = useState(false);
	const [cycleChecked, setCycleChecked] = useState(false);
	const [statusChecked, setStatusChecked] = useState(false);

	const currentCycle = useSelector(
		(state: any) =>
			state.contribution?.contributionCycleByCircle?.[circle._id] ?? null,
	);
	const hasMissedForCircle = useSelector((state: any) =>
		(state.autoDraft?.missedContributions ?? []).some(
			(mc: any) => mc.unionId === circle._id && mc.missedCount > 0,
		),
	);
	const contributionStatus = useSelector(selectMemberContribution);
	const status: IContributionStatus | undefined =
		contributionStatus?.[`${circle._id}-${currentUserId}`];

	// Refetch whenever the circle object is reloaded (e.g. after contributing)
	useEffect(() => {
		let active = true;
		Promise.resolve(
			dispatch(getCurrentCycleOfCircle({ unionId: circle._id })),
		).finally(() => active && setCycleChecked(true));
		return () => {
			active = false;
		};
	}, [circle, dispatch]);

	useEffect(() => {
		if (!circle?._id || !currentUserId) {
			setStatusChecked(true);
			return;
		}
		let active = true;
		Promise.resolve(
			dispatch(
				getContributionStatus({ unionId: circle._id, memberId: currentUserId }),
			),
		).finally(() => active && setStatusChecked(true));
		return () => {
			active = false;
		};
	}, [circle, currentUserId, dispatch]);

	const isChecking = !cycleChecked || !statusChecked;

	const availability = getContributionAvailability({
		circle,
		currentCycle,
		status,
		currentUserId,
		justContributed,
	});
	const statusLine = describeStatus(availability, circle, currentCycle);

	const configuration = circle?.configuration ?? {};
	// Modal keeps the original fallback; display guards against "" (Intl throws on it)
	const modalCurrency = configuration.currency ?? "usd";
	const currency = configuration.currency || "usd";
	const amountLabel = formatCurrency(
		Number(configuration.contributionAmount) || 0,
		currency,
	);
	const contributionDays: string[] = configuration.contributionDays ?? [];
	const todayName = moment().format("dddd");
	const leaderName =
		circle?.leader ?
			`${circle.leader.firstName ?? ""} ${circle.leader.lastName ?? ""}`.trim()
		:	"";
	const memberCount = circle?.members?.length ?? 0;
	const accent =
		isChecking ? null
		: hasMissedForCircle ? "#f97316"
		: availability.canContribute ? colors.mainSecondary
		: null;

	const openContribution = (
		totalMissedAmount?: number | null,
		unpaidDates?: string[],
		contributionDayToday?: boolean,
		todayAlreadyPaid?: boolean,
	) => {
		setMissedContributionAmount(totalMissedAmount || null);
		setMissedDates(unpaidDates || []);
		setIsContributionDayToday(contributionDayToday || false);
		setIsTodayAlreadyPaid(todayAlreadyPaid || false);
		setOpenContributionModal(true);
	};

	const closeContribution = () => {
		setOpenContributionModal(false);
		setMissedContributionAmount(null);
		setMissedDates([]);
		setIsContributionDayToday(false);
		setIsTodayAlreadyPaid(false);
	};

	return (
		<>
			<Card
				elevation={0}
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					borderRadius: "16px",
					border: "1px solid",
					borderColor: accent ? alpha(accent, 0.6) : "#e6ebf1",
					boxShadow:
						accent ?
							`0 8px 24px ${alpha(accent, 0.18)}`
						:	"0 2px 10px rgba(15, 23, 42, 0.05)",
					overflow: "visible",
					transition: "box-shadow 0.2s ease, border-color 0.2s ease",
					"&:hover": {
						boxShadow: "0 10px 28px rgba(15, 23, 42, 0.10)",
					},
				}}
			>
				{/* Identity */}
				<Stack
					direction="row"
					spacing={1.5}
					alignItems="center"
					sx={{ p: 2, pb: 1.5 }}
				>
					<Avatar
						src={circle?.circleImage}
						alt={circle?.circleName}
						sx={{
							width: 48,
							height: 48,
							bgcolor: colors.primary,
							fontWeight: 700,
						}}
					>
						{circle?.circleName?.charAt(0)?.toUpperCase()}
					</Avatar>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography
							sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#0f172a" }}
							noWrap
						>
							{circle?.circleName}
						</Typography>
						{leaderName && (
							<Typography variant="body2" sx={{ color: "#64748b" }} noWrap>
								Led by {leaderName}
							</Typography>
						)}
					</Box>
				</Stack>

				{/* Amount and schedule */}
				<Box sx={{ px: 2 }}>
					<Stack direction="row" alignItems="baseline" spacing={1}>
						<Typography
							sx={{
								fontSize: { xs: "1.75rem", sm: "1.9rem" },
								fontWeight: 800,
								lineHeight: 1.1,
								color: colors.primary,
							}}
						>
							{amountLabel}
						</Typography>
						<Typography variant="body2" sx={{ color: "#64748b" }}>
							per contribution
						</Typography>
					</Stack>

					<Stack
						direction="row"
						spacing={2}
						sx={{ mt: 1, color: "#475569" }}
						alignItems="center"
					>
						<Stack direction="row" spacing={0.5} alignItems="center">
							<AutorenewRoundedIcon sx={{ fontSize: 18 }} />
							<Typography variant="body2">
								{configuration.contributionPeriod || "—"}
							</Typography>
						</Stack>
						<Stack direction="row" spacing={0.5} alignItems="center">
							<GroupsRoundedIcon sx={{ fontSize: 18 }} />
							<Typography variant="body2">
								{memberCount} {memberCount === 1 ? "member" : "members"}
							</Typography>
						</Stack>
					</Stack>

					{/* Week strip: contribution days filled, today outlined */}
					<Box
						role="list"
						aria-label={
							contributionDays.length ?
								`Contributes on ${contributionDays.join(", ")}`
							:	"No contribution days set"
						}
						sx={{ display: "flex", gap: 0.5, mt: 1.5 }}
					>
						{WEEK_DAYS.map((day) => {
							const isActive = contributionDays.includes(day);
							const isToday = day === todayName;
							return (
								<Box
									key={day}
									role="listitem"
									aria-label={`${day}${isActive ? ", contribution day" : ""}${isToday ? ", today" : ""}`}
									sx={{
										flex: 1,
										minWidth: 0,
										py: 0.6,
										borderRadius: "8px",
										textAlign: "center",
										fontSize: "0.72rem",
										fontWeight: isActive || isToday ? 700 : 500,
										color: isActive ? colors.primary : "#94a3b8",
										bgcolor:
											isActive ? alpha(colors.mainSecondary, 0.16) : "#f1f5f9",
										border: "1.5px solid",
										borderColor: isToday ? colors.primary : "transparent",
									}}
								>
									{day.slice(0, 3)}
								</Box>
							);
						})}
					</Box>
				</Box>

				<Box sx={{ flexGrow: 1 }} />

				{/* Status + actions */}
				<MissedContributionsWrapper
					unionId={circle._id}
					fullWidth
					showBadge={false}
					onContributeClick={(
						unionId,
						totalDueDollars,
						unpaidDates,
						contributionDayToday,
						todayPaid,
					) => {
						if (unionId === circle._id) {
							openContribution(
								totalDueDollars,
								unpaidDates,
								contributionDayToday,
								todayPaid,
							);
						}
					}}
				>
					{({
						hasMissedContributions,
						missedCount,
						totalDueDollars,
						openMissedContributions,
					}) => {
						const line: StatusLine =
							isChecking ?
								{
									tone: "neutral",
									icon: (
										<CircularProgress size={14} thickness={5} color="inherit" />
									),
									text: "Checking contribution status…",
								}
							: hasMissedContributions ?
								{
									tone: "warning",
									icon: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />,
									text: `${missedCount} missed contribution${missedCount !== 1 ? "s" : ""} · ${formatCurrency(totalDueDollars, currency)} due`,
								}
							:	statusLine;

						const canPay = !isChecking && availability.canContribute;

						return (
							<Box sx={{ p: 2, pt: 1.5 }}>
								<Stack
									direction="row"
									spacing={0.75}
									alignItems="center"
									role="status"
									sx={{
										color: TONE_COLOR[line.tone],
										mb: 1.25,
										minHeight: 22,
									}}
								>
									{line.icon}
									<Typography
										variant="body2"
										sx={{ fontWeight: 600, color: "inherit", lineHeight: 1.35 }}
									>
										{line.text}
									</Typography>
								</Stack>

								<Stack direction="row" spacing={1}>
									{hasMissedContributions && !isChecking ?
										<Button
											fullWidth
											variant="contained"
											disableElevation
											onClick={openMissedContributions}
											sx={{
												...actionButtonSx,
												background: MISSED_GRADIENT,
												"&:hover": {
													background: MISSED_GRADIENT,
													filter: "brightness(0.95)",
												},
											}}
										>
											Pay missed contributions
										</Button>
									:	<Button
											fullWidth
											variant="contained"
											disableElevation
											disabled={!canPay}
											onClick={() => openContribution()}
											startIcon={
												availability.reason === "already_contributed" ?
													<CheckCircleRoundedIcon />
												:	undefined
											}
											sx={{
												...actionButtonSx,
												bgcolor: colors.blueRibbon,
												"&:hover": { bgcolor: "#0f2d80" },
												"&.Mui-disabled": {
													bgcolor: "#e2e8f0",
													color: "#64748b",
												},
											}}
										>
											{canPay ?
												`Contribute ${amountLabel}`
											: availability.reason === "already_contributed" ?
												"Contributed today"
											:	"Contribute"}
										</Button>
									}

									<Button
										variant="outlined"
										onClick={() => onViewCircle(circle._id)}
										aria-label={`View ${circle?.circleName ?? "circle"}`}
										endIcon={<ArrowForwardRoundedIcon />}
										sx={{
											minHeight: 48,
											flexShrink: 0,
											borderRadius: "12px",
											textTransform: "none",
											fontWeight: 600,
											color: colors.primary,
											borderColor: "#cbd5e1",
											"&:hover": {
												borderColor: colors.primary,
												bgcolor: alpha(colors.primary, 0.04),
											},
											[NARROW]: {
												minWidth: 48,
												px: 0,
												"& .view-label": { display: "none" },
												"& .MuiButton-endIcon": { m: 0 },
											},
										}}
									>
										<span className="view-label">View</span>
									</Button>
								</Stack>
							</Box>
						);
					}}
				</MissedContributionsWrapper>
			</Card>

			<ContributionModal
				open={openContributionModal}
				handleClose={closeContribution}
				unionId={circle._id}
				memberId={currentUserId}
				contributionPeriod={configuration.contributionPeriod}
				contributionAmount={configuration.contributionAmount}
				currency={modalCurrency}
				isMissedContribution={missedContributionAmount !== null}
				missedDates={missedDates}
				isContributionDayToday={isContributionDayToday}
				isTodayAlreadyPaid={isTodayAlreadyPaid}
				onContributionSuccess={() => {
					setJustContributed(true);
					onContributionSuccess?.();
				}}
			/>
		</>
	);
};

export default CircleContributionCard;
